// ============================================
// Project Types
// ============================================

export interface ProjectAttribute {
  id?: number;
  title: string;
  price: number;
  order: number;
  duration?: string;
  endingTime?: string;
  numberOfBeneficiariesNames?: number;
  verificationStartDate?: string;
  verificationEndDate?: string;
}

export interface ProjectLicenceCode {
  id?: number;
  code: string;
  startDate: string;
  endDate: string;
}

export interface ProjectVerificationLimitation {
  minNumberOfPhotos: number;
  maxNumberOfPhotos: number;
  minNumberOfVideos: number;
  maxNumberOfVideos: number;
  maxPhotoSize: number;
  maxVideoSize: number;
  verificationStageNumber: number;
  verificationStageName: string;
  smsMessage: string;
  smsSendingTime: string;
  cutoffTime: string;
}
export interface ProjectVerificationLimitationResponse extends ProjectVerificationLimitation {
  stageNumber: number;
  stageName: string;
  smsMessage: string;
}

export interface Classification {
  id: number;
  name: string;
}

export interface ProjectBankAccount {
  bankAccountId: number;
}

export interface EndowmentCertificateDesign {
  content: string;
  show: boolean;
  color: string;
  xPercentage: number;
  yPercentage: number;
  fontSize: number;
  alignment: number;
  hasShadow: boolean;
  disclaimer: number;
  "shadow.x"?: number;
  "shadow.y"?: number;
  "shadow.blur"?: number;
  "shadow.color"?: string;
  "border.color"?: string;
  "border.thickness"?: number;
}

export interface GiftProject {
  id: number;
  name: string;
}

export interface Project {
  id?: number;
  name: string;
  title: string;
  slug: string;
  shortDescription: string;
  summary: string;
  projectDetails: string;
  category: number;
  order: number;
  startsFrom: string;
  endsTo: string;
  allRequiredAmount: number;
  currentPaidAmount: number;
  imageUrl?: string;
  imageFullUrl?: string;
  projectDescriptionImages?: string;
  projectDescriptionImagesUrls?: string;
  imageAlt?: string;
  projectLicenceUrl?: string;
  certificateUrl?: string;
  websiteUrl?: string;
  projectLicenceCodes?: ProjectLicenceCode[];
  isActive: boolean;
  isArchived: boolean;
  showOnHomePage: boolean;
  showForFastDonation: boolean;
  showProgressBar: boolean;
  isFastPaymentActive: boolean;
  haveCertificate: boolean;
  hideProjectFromStore: boolean;
  notifyWhenCompleted: boolean;
  enableVerification: boolean;
  allowOpenDonation: boolean;
  projectVisualEffect: boolean;
  isOnBehalfOnProject: boolean;
  minimumOpenDonationAmount: number;
  personEmailToNotify?: string;
  personPhoneToNotify?: string;
  seoTitle: string;
  seoKeywords: string;
  seoDescription: string;
  adahiProductId?: number;
  adahiRitualId?: number;
  giftProjectId?: number;
  projectAccountShowingOption: number;
  classifications: Classification[];
  projectAttributes: ProjectAttribute[];
  projectBankAccounts: ProjectBankAccount[];
  projectVerificationLimitation: ProjectVerificationLimitationResponse[];
  endowmentCertificateDesign?: EndowmentCertificateDesign;
  giftProject?: GiftProject;
  // Computed/Display fields
  requiredAmount?: number;
  totalAmount?: number;
  donationCount?: number;
  progressPercentage?: number;

  // theme  themeId?: number;
  themeUrl?: string;
  themeId?: number;
}

// ============================================
// Form Types
// ============================================

export interface CardDesignFormValues {
  x: number;
  y: number;
  xWithTextWidth: number;
  shadowX: number;
  shadowY: number;
  show: boolean;
  fontSize: number;
  alignment: number;
  disclaimer: number;
  shadowBlur: number;
  content?: string;
  color: string;
  hasShadow: boolean;
  borderThickness: number;
  borderColor: string;
  shadowColor: string;
}

export interface ProjectFormValues {
  // Basic Information
  id?: number;
  name: string;
  title: string;
  slug: string;
  shortDescription: string;
  summary: string;
  projectDetails: string;
  projectType: number | string;
  order: number;

  // Images
  image: File | null;
  projectDescriptionImages: File | null;
  imageUrl?: string;
  imageAlt?: string;
  projectLicence: File | null;
  projectLicenceUrl?: string;
  certificate: File | null;
  certificateUrl: string | null;
  certificateView: string | null;

  // Dates & Amounts
  startsFrom: string;
  endsTo: string;
  allRequiredAmount: number;
  currentPaidAmount: number;
  minimumOpenDonationAmount: number;

  // Settings
  classificationIds: number[];
  giftProjectId?: number;
  projectAccountShowingOption: number;
  bankAccountsIds: number[];
  bankAccountsIdsArray?: number[];

  // Toggles
  showOnHomePage: boolean;
  showForFastDonation: boolean;
  showProgressBar: boolean;
  isFastPaymentActive: boolean;
  haveCertificate: boolean;
  hideProjectFromStore: boolean;
  notifyWhenCompleted: boolean;
  enableVerification: boolean;
  allowOpenDonation: boolean;
  projectVisualEffect: boolean;
  isOnBehalfOnProject: boolean;

  // Notification
  personEmailToNotify?: string;
  personPhoneToNotify?: string;

  // Verification Settings
  projectVerificationLimitations: ProjectVerificationLimitation[];
  projectVerificationLimitation?: ProjectVerificationLimitation[];

  // Project Attributes
  projectAttributes: ProjectAttribute[];

  // SEO
  seoTitle: string;
  seoKeywords: string;
  seoDescription: string;

  // Adahi
  adahiProductId?: number;
  adahiRitualId?: number;

  // Certificate Design
  endowmentCertificateDesign: CardDesignFormValues[];

  // Customer Campaign
  customerSuggestedCampaignId?: number;
  customerSuggestedCampaignName?: string;
  websiteUrl?: string;

  // Licences
  projectLicenceCodes: ProjectLicenceCode[];

  themeId?: number;
}

// ============================================
// API & Query Types
// ============================================

export interface Bank {
  id: number;
  name: string;
  imageFullUrl: string;
  bankAccounts: BankAccount[];
}

export interface BankAccount {
  id: number;
  name: string;
  iban: string;
  bankName?: string;
  bankImageFullUrl?: string;
}

export interface Category {
  id: number;
  name: string;
}

export interface AdahiProduct {
  id: number;
  name: string;
}

export interface AdahiRitual {
  id: number;
  name: string;
  brief: string;
}

export interface CustomerCampaign {
  id: number;
  projectTitle: string;
  userName: string;
  requiredAmount?: number;
  classificationId?: number;
  classificationName?: string;
}

export interface ProjectDataResponse {
  banks: { data: Bank[] };
  categories: Category[];
  giftProjects: GiftProject[];
}

export interface AdahiDataResponse {
  adahiProducts: AdahiProduct[];
  adahiRitual: AdahiRitual[];
}

export interface ProjectsListResponse {
  data: Project[];
  totalItems: number;
}

// ============================================
// Search & Pagination Types
// ============================================

export interface SearchValues {
  title: string;
  toDate: string;
  fromDate: string;
  toAmount: string;
  isActive: string;
  isArchived: string;
  fromAmount: string;
  isCompleted: string;
  showInHomePage: string;
  classificationIds: string;
}

export interface PaginationState {
  page: number;
  size: number;
}

export interface QueryParams {
  page: number;
  perSize: number;
  search: SearchValues;
}

// ============================================
// Options Types
// ============================================

export interface SelectOption<T = number> {
  value: T;
  label: string;
}

export enum ProjectType {
  Grades = 0,
  Units = 1,
  Open = 2,
  Deduction = 3,
  Meal = 4,
}

export interface DonateTypeOption {
  value: ProjectType;
  name: string;
}

export interface AttributeDurationOption {
  value: string;
  name: string;
}

export enum BankAccountDisplayOption {
  Hidden = 0,
  Above = 1,
  Below = 2,
}

export interface BankAccountOption {
  value: BankAccountDisplayOption;
  name: string;
}

// ============================================
// Table Types
// ============================================

export interface TableRow extends Omit<Project, "classifications"> {
  classifications: string[];
}

export interface BankTableRow extends BankAccount {
  bankName: string;
  bankImageFullUrl: string;
}

// ============================================
// Component Props Types
// ============================================

export interface TableActionsProps {
  row: Project;
  deleteHandler: (id: number) => Promise<void>;
  ToggleActive: (id: number, value: boolean) => Promise<void>;
  ToggleArchive: (id: number, value: boolean) => Promise<void>;
}

export interface CustomerCampaignInfoProps {
  customerCampaign: CustomerCampaign | null;
}

export interface CertificateCardDesignProps {
  title: string;
  close: () => void;
  save: (designs: CardDesignFormValues[]) => void;
  design: CardDesignFormValues[];
  url: string;
}

export interface MultiSearchProps {
  onSubmit: () => void;
  search: {
    show: boolean;
    values: SearchValues;
  };
  updateSearch: (params: { show?: boolean; values?: SearchValues }) => void;
  onReset: () => void;
}
